import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "../contexts/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import {
  CaretLeft, FloppyDisk, Clock, Target, ListNumbers, Shuffle,
  ShieldCheck, Eye, PencilSimple, VideoCamera, ArrowsOut, Trash,
  CircleNotch, ArrowsClockwise, Plus, Check
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AssessmentSettingsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assessment, setAssessment] = useState(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("draft");
  const [timeLimit, setTimeLimit] = useState(30);
  const [passingScore, setPassingScore] = useState(70);
  const [randomizedOrder, setRandomizedOrder] = useState(true);
  const [allowReview, setAllowReview] = useState(true);
  const [allowChangeAnswers, setAllowChangeAnswers] = useState(true);
  const [requireFullscreen, setRequireFullscreen] = useState(true);
  const [enableWebcam, setEnableWebcam] = useState(false);

  const [questions, setQuestions] = useState([]);
  const [editIdx, setEditIdx] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [addingNew, setAddingNew] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/assessments/${id}`, { withCredentials: true });
        const a = res.data;
        setAssessment(a);
        setQuestions(a.questions || []);
        setTitle(a.title || "");
        setDescription(a.description || "");
        setStatus(a.status || "draft");
        const s = a.settings || {};
        setTimeLimit(s.time_limit ?? 30);
        setPassingScore(s.passing_score ?? 70);
        setRandomizedOrder(s.randomized_order ?? true);
        setAllowReview(s.allow_review ?? true);
        setAllowChangeAnswers(s.allow_change_answers ?? true);
        setRequireFullscreen(s.require_fullscreen ?? true);
        setEnableWebcam(s.enable_webcam ?? false);
      } catch {
        toast.error("Failed to load assessment");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const handleSave = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/assessments/${id}`, {
        title,
        description,
        status,
        settings: {
          time_limit: timeLimit,
          passing_score: passingScore,
          question_count: assessment?.question_count || 10,
          randomized_order: randomizedOrder,
          difficulty_distribution: assessment?.settings?.difficulty_distribution || { easy: 30, medium: 50, hard: 20 },
          allow_review: allowReview,
          allow_change_answers: allowChangeAnswers,
          require_fullscreen: requireFullscreen,
          enable_webcam: enableWebcam,
        }
      }, { withCredentials: true });
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this assessment and all associated data? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/assessments/${id}`, { withCredentials: true });
      toast.success("Assessment deleted");
      navigate("/dashboard");
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleDeleteQuestion = async (qId) => {
    try {
      await axios.delete(`${API}/questions/${qId}`, { withCredentials: true });
      setQuestions(prev => prev.filter(q => q.question_id !== qId));
      toast.success("Question deleted");
    } catch { toast.error("Delete failed"); }
  };

  const handleRegenerate = async (qId) => {
    try {
      const res = await axios.post(`${API}/questions/${qId}/regenerate`, {}, { withCredentials: true });
      setQuestions(prev => prev.map(q => q.question_id === qId ? res.data : q));
      toast.success("Question regenerated");
    } catch { toast.error("Regeneration failed"); }
  };

  const startEdit = (idx) => {
    setEditIdx(idx);
    const q = questions[idx];
    setEditForm({
      question_text: q.question_text,
      question_type: q.question_type,
      options: q.options.map(o => ({ ...o })),
      difficulty: q.difficulty,
      skill_tag: q.skill_tag,
      explanation: q.explanation
    });
  };

  const saveEdit = async () => {
    const q = questions[editIdx];
    try {
      const res = await axios.put(`${API}/questions/${q.question_id}`, editForm, { withCredentials: true });
      setQuestions(prev => prev.map((item, i) => i === editIdx ? res.data : item));
      setEditIdx(null);
      setEditForm(null);
      toast.success("Question updated");
    } catch { toast.error("Update failed"); }
  };

  const handleAddQuestion = async () => {
    try {
      const res = await axios.post(`${API}/assessments/${id}/questions`, editForm, { withCredentials: true });
      setQuestions(prev => [...prev, res.data]);
      setAddingNew(false);
      setEditForm(null);
      toast.success("Question added");
    } catch { toast.error("Add failed"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-14 flex items-center justify-center" data-testid="settings-loading">
        <CircleNotch size={32} className="text-[#FF3366] animate-spin" />
      </div>
    );
  }

  const SettingRow = ({ icon: Icon, label, desc, children }) => (
    <div className="flex items-center justify-between py-4">
      <div className="flex items-start gap-3 flex-1 min-w-0 mr-4">
        <Icon size={18} className="text-[#52525B] shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#0A0A0A]">{label}</p>
          <p className="text-xs text-[#52525B] mt-0.5">{desc}</p>
        </div>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );

  return (
    <div className="min-h-screen pt-14 bg-white" data-testid="assessment-settings-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-8">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-1 text-xs text-[#52525B] hover:text-[#0A0A0A] mb-4"
          data-testid="back-link"
        >
          <CaretLeft size={14} /> Dashboard
        </button>

        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl tracking-tight text-[#0A0A0A]">
              Assessment Settings
            </h1>
            <p className="text-sm text-[#52525B] mt-1">
              {assessment?.question_count || 0} questions &middot; {assessment?.candidate_count || 0} candidates invited
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#FF3366] text-white hover:bg-[#E62E5C] rounded-none px-5 py-2.5 h-auto font-semibold text-sm"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <CircleNotch size={16} className="mr-1.5 animate-spin" />
            ) : (
              <FloppyDisk size={16} className="mr-1.5" />
            )}
            Save Settings
          </Button>
        </div>

        <section className="border border-black/10 mb-6" data-testid="general-settings">
          <div className="px-6 py-4 border-b border-black/10 bg-[#F8F9FA]">
            <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A0A0A]">General</span>
          </div>
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] mb-1.5 block">
                Title
              </label>
              <Input
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="rounded-none border-black/20 focus:border-black focus:ring-0"
                data-testid="settings-title-input"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] mb-1.5 block">
                Description
              </label>
              <Textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                className="rounded-none border-black/20 focus:border-black focus:ring-0 resize-none text-sm"
                data-testid="settings-desc-input"
              />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] mb-1.5 block">
                Status
              </label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="rounded-none border-black/20 w-48" data-testid="settings-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        <section className="border border-black/10 mb-6" data-testid="param-settings">
          <div className="px-6 py-4 border-b border-black/10 bg-[#F8F9FA]">
            <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A0A0A]">Assessment Parameters</span>
          </div>
          <div className="px-6 divide-y divide-black/5">
            <SettingRow icon={Clock} label="Time Limit" desc="Duration in minutes candidates have to complete the assessment">
              <div className="flex items-center gap-2">
                <Input
                  type="number" min={1} max={300}
                  value={timeLimit}
                  onChange={e => setTimeLimit(Number(e.target.value))}
                  className="rounded-none border-black/20 w-20 text-center text-sm"
                  data-testid="settings-time-input"
                />
                <span className="text-xs text-[#52525B]">min</span>
              </div>
            </SettingRow>
            <SettingRow icon={Target} label="Passing Score" desc="Minimum percentage to pass the assessment">
              <div className="flex items-center gap-2">
                <Input
                  type="number" min={0} max={100}
                  value={passingScore}
                  onChange={e => setPassingScore(Number(e.target.value))}
                  className="rounded-none border-black/20 w-20 text-center text-sm"
                  data-testid="settings-score-input"
                />
                <span className="text-xs text-[#52525B]">%</span>
              </div>
            </SettingRow>
            <SettingRow icon={Shuffle} label="Randomize Question Order" desc="Shuffle questions for each candidate">
              <Switch checked={randomizedOrder} onCheckedChange={setRandomizedOrder} data-testid="settings-randomize-toggle" />
            </SettingRow>
          </div>
        </section>

        <section className="border border-black/10 mb-6" data-testid="proctoring-settings">
          <div className="px-6 py-4 border-b border-black/10 bg-[#F8F9FA]">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#0A0A0A]" />
              <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A0A0A]">Proctoring & Security</span>
            </div>
          </div>
          <div className="px-6 divide-y divide-black/5">
            <SettingRow icon={ArrowsOut} label="Require Fullscreen" desc="Candidate must enter fullscreen before starting. Exits are tracked.">
              <Switch checked={requireFullscreen} onCheckedChange={setRequireFullscreen} data-testid="settings-fullscreen-toggle" />
            </SettingRow>
            <SettingRow icon={Eye} label="Allow Answer Review" desc="Candidate can see all answers before final submission">
              <Switch checked={allowReview} onCheckedChange={setAllowReview} data-testid="settings-review-toggle" />
            </SettingRow>
            <SettingRow icon={PencilSimple} label="Allow Changing Answers" desc="Candidate can modify an answer after selecting it">
              <Switch checked={allowChangeAnswers} onCheckedChange={setAllowChangeAnswers} data-testid="settings-change-toggle" />
            </SettingRow>
            <SettingRow icon={VideoCamera} label="Webcam Proctoring" desc="Capture periodic webcam snapshots during the assessment">
              <Switch checked={enableWebcam} onCheckedChange={setEnableWebcam} data-testid="settings-webcam-toggle" />
            </SettingRow>
          </div>
        </section>

        <section className="border border-black/10 mb-6" data-testid="questions-section">
          <div className="px-6 py-4 border-b border-black/10 bg-[#F8F9FA] flex items-center justify-between">
            <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A0A0A]">Questions ({questions.length})</span>
          </div>
          <div className="px-6 py-5 space-y-3">
            {questions.length === 0 ? (
              <p className="text-sm text-[#52525B]">No questions yet. Add one below.</p>
            ) : (
              questions.map((q, idx) => (
                <div key={q.question_id} className="border border-black/10" data-testid={`question-card-${idx}`}>
                  {editIdx === idx ? (
                    <div className="p-4 space-y-3">
                      <textarea
                        value={editForm.question_text}
                        onChange={e => setEditForm({ ...editForm, question_text: e.target.value })}
                        className="w-full rounded-none border border-black/20 text-sm p-2 focus:outline-none focus:border-black"
                        rows={2}
                        data-testid={`edit-question-text-${idx}`}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        {editForm.options.map((opt, oi) => (
                          <div key={oi} className="flex items-center gap-2">
                            <input type="checkbox" checked={opt.is_correct}
                              onChange={() => {
                                const newOpts = [...editForm.options];
                                if (editForm.question_type === "single") {
                                  newOpts.forEach(o => o.is_correct = false);
                                }
                                newOpts[oi].is_correct = !newOpts[oi].is_correct;
                                setEditForm({ ...editForm, options: newOpts });
                              }}
                              className="w-4 h-4" />
                            <input
                              value={opt.text}
                              onChange={e => {
                                const newOpts = [...editForm.options];
                                newOpts[oi].text = e.target.value;
                                setEditForm({ ...editForm, options: newOpts });
                              }}
                              className="w-full rounded-none border border-black/20 text-sm p-1.5 focus:outline-none focus:border-black"
                              data-testid={`edit-option-${idx}-${oi}`} />
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <select value={editForm.difficulty}
                          onChange={e => setEditForm({ ...editForm, difficulty: e.target.value })}
                          className="rounded-none border border-black/20 text-sm px-2 py-1.5 focus:outline-none focus:border-black"
                          data-testid={`edit-difficulty-${idx}`}>
                          <option value="easy">Easy</option>
                          <option value="medium">Medium</option>
                          <option value="hard">Hard</option>
                        </select>
                        <input
                          value={editForm.skill_tag}
                          onChange={e => setEditForm({ ...editForm, skill_tag: e.target.value })}
                          placeholder="Skill tag"
                          className="rounded-none border border-black/20 text-sm px-2 py-1.5 flex-1 focus:outline-none focus:border-black"
                          data-testid={`edit-skill-${idx}`} />
                        <Button onClick={saveEdit} className="bg-[#0A0A0A] text-white rounded-none px-4 h-8 text-xs" data-testid={`save-edit-${idx}`}>
                          Save
                        </Button>
                        <Button variant="ghost" onClick={() => { setEditIdx(null); setEditForm(null); }} className="rounded-none h-8 text-xs" data-testid={`cancel-edit-${idx}`}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="text-xs font-bold text-[#52525B] tabular-nums">Q{idx + 1}</span>
                            <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase
                              ${q.difficulty === 'easy' ? 'bg-[#10B981]/10 text-[#10B981]' :
                                q.difficulty === 'hard' ? 'bg-[#EF4444]/10 text-[#EF4444]' :
                                'bg-[#F59E0B]/10 text-[#F59E0B]'}`}>
                              {q.difficulty}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 bg-[#F8F9FA] text-[#52525B] font-medium">{q.skill_tag}</span>
                            <span className="text-[10px] px-1.5 py-0.5 bg-[#F8F9FA] text-[#52525B] font-medium">{q.question_type}</span>
                          </div>
                          <p className="text-sm text-[#0A0A0A] mb-3">{q.question_text}</p>
                          <div className="grid grid-cols-2 gap-1.5">
                            {q.options?.map((opt, oi) => (
                              <div key={oi}
                                className={`text-xs px-3 py-1.5 border ${opt.is_correct ? 'border-[#10B981] bg-[#10B981]/5 text-[#10B981]' : 'border-black/10 text-[#52525B]'}`}>
                                {opt.text} {opt.is_correct && <Check size={12} className="inline ml-1" />}
                              </div>
                            ))}
                          </div>
                          {q.explanation && (
                            <p className="text-xs text-[#52525B] mt-2 italic">Explanation: {q.explanation}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button onClick={() => startEdit(idx)} className="p-1.5 hover:bg-black/5" data-testid={`edit-q-${idx}`}>
                                <PencilSimple size={14} className="text-[#52525B]" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="rounded-none bg-[#0A0A0A] text-white border-none text-[11px] px-2 py-1">Edit question</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button onClick={() => handleRegenerate(q.question_id)} className="p-1.5 hover:bg-black/5" data-testid={`regen-q-${idx}`}>
                                <ArrowsClockwise size={14} className="text-[#52525B]" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="rounded-none bg-[#0A0A0A] text-white border-none text-[11px] px-2 py-1">Regenerate</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button onClick={() => handleDeleteQuestion(q.question_id)} className="p-1.5 hover:bg-[#EF4444]/10" data-testid={`delete-q-${idx}`}>
                                <Trash size={14} className="text-[#EF4444]" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="rounded-none bg-[#0A0A0A] text-white border-none text-[11px] px-2 py-1">Delete question</TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}

            {addingNew ? (
              <div className="border border-black/10 p-4 space-y-3 bg-[#F8F9FA]" data-testid="add-question-form">
                <textarea
                  value={editForm?.question_text || ""}
                  onChange={e => setEditForm({ ...editForm, question_text: e.target.value })}
                  placeholder="Question text"
                  className="w-full rounded-none border border-black/20 text-sm p-2 focus:outline-none focus:border-black"
                  rows={2}
                  data-testid="new-question-text"
                />
                <div className="grid grid-cols-2 gap-2">
                  {(editForm?.options || []).map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <input type="checkbox" checked={opt.is_correct}
                        onChange={() => {
                          const newOpts = [...editForm.options];
                          newOpts[oi].is_correct = !newOpts[oi].is_correct;
                          setEditForm({ ...editForm, options: newOpts });
                        }} className="w-4 h-4" />
                      <input
                        value={opt.text}
                        onChange={e => {
                          const newOpts = [...editForm.options];
                          newOpts[oi].text = e.target.value;
                          setEditForm({ ...editForm, options: newOpts });
                        }}
                        placeholder={`Option ${oi + 1}`}
                        className="w-full rounded-none border border-black/20 text-sm p-1.5 focus:outline-none focus:border-black" />
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <select value={editForm?.difficulty || "medium"}
                    onChange={e => setEditForm({ ...editForm, difficulty: e.target.value })}
                    className="rounded-none border border-black/20 text-sm px-2 py-1.5 focus:outline-none focus:border-black">
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                  <input
                    value={editForm?.skill_tag || ""}
                    onChange={e => setEditForm({ ...editForm, skill_tag: e.target.value })}
                    placeholder="Skill tag"
                    className="rounded-none border border-black/20 text-sm px-2 py-1.5 flex-1 focus:outline-none focus:border-black" />
                  <Button onClick={handleAddQuestion} className="bg-[#FF3366] text-white rounded-none px-4 h-8 text-xs" data-testid="confirm-add-btn">Add</Button>
                  <Button variant="ghost" onClick={() => { setAddingNew(false); setEditForm(null); }} className="rounded-none h-8 text-xs" data-testid="cancel-add-btn">Cancel</Button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  setAddingNew(true);
                  setEditForm({
                    question_text: "", question_type: "single",
                    options: [
                      { text: "", is_correct: false }, { text: "", is_correct: false },
                      { text: "", is_correct: false }, { text: "", is_correct: false }
                    ],
                    difficulty: "medium", skill_tag: "", explanation: ""
                  });
                }}
                className="w-full border border-dashed border-black/20 py-3 text-sm text-[#52525B] hover:text-[#0A0A0A] hover:border-black/40 transition-colors flex items-center justify-center gap-2"
                data-testid="add-question-btn"
              >
                <Plus size={16} /> Add Custom Question
              </button>
            )}
          </div>
        </section>

        <section className="border border-black/10 mb-6" data-testid="quick-links">
          <div className="px-6 py-4 border-b border-black/10 bg-[#F8F9FA]">
            <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A0A0A]">Quick Links</span>
          </div>
          <div className="px-6 py-4 flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(`/assessments/${id}/results`)}
              className="rounded-none border-black/20 text-xs"
              data-testid="link-results"
            >
              View Results
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/assessments/${id}/invite`)}
              className="rounded-none border-black/20 text-xs"
              data-testid="link-invite"
            >
              Invite Candidates
            </Button>
          </div>
        </section>

        <section className="border border-[#EF4444]/30 mb-6" data-testid="danger-zone">
          <div className="px-6 py-4 border-b border-[#EF4444]/20 bg-[#EF4444]/5">
            <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#EF4444]">Danger Zone</span>
          </div>
          <div className="px-6 py-5 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#0A0A0A]">Delete Assessment</p>
              <p className="text-xs text-[#52525B]">Permanently remove this assessment and all related data (questions, invitations, results).</p>
            </div>
            <Button
              variant="outline"
              onClick={handleDelete}
              className="rounded-none border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white text-xs"
              data-testid="delete-assessment-btn"
            >
              <Trash size={14} className="mr-1.5" /> Delete
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}
